// pages/superadmin/ScoringRulesPage.tsx
import React, { useState, useEffect } from "react";
import { Section, Trait, Exam } from "../../types";
import { Scale } from "../../types/scale.types";
import { useApp } from "../../context/AppContext";
import { getSections } from "../../services/sectionService";
import { getTraits } from "../../services/traitService";
import { getExams } from "../../services/examService";
import { getScales } from "../../services/scaleService";
import { 
  getScoringRules, 
  createScoringRule, 
  updateScoringRule, 
  deleteScoringRule,
  ScoringRule,
  ScoringRuleForm
} from "../../services/scoringRuleService";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table, { Column } from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import { Input, Select, Textarea, Toggle } from "../../components/ui/FormFields";
import { toast } from "react-hot-toast";

const DEFAULT_SCORING_RULE: ScoringRuleForm = {
  name: "",
  description: "",
  scope: "global",
  scopeId: "",
  rawScoreCalculation: {
    method: "average",
    formula: "sum(question_values) / count(questions)"
  },
  reverseScoring: {
    enabled: true,
    formula: "(maxScale + 1) - value"
  },
  traitAggregation: {
    method: "average"
  },
  normalization: {
    enabled: true,
    minValue: 0,
    maxValue: 5,
    targetMin: 0,
    targetMax: 100,
    method: "min_max"
  },
  sectionScoring: {
    calculateSectionWise: true
  },
  overallIndex: {
    enabled: true,
    formula: "(sum(section_scores * section_weightage) / sum(weightage)) * 100",
    interpretation: "0-25: Low, 26-50: Moderate, 51-75: High, 76-100: Very High"
  },
  isActive: true
};

const ScoringRulesPage: React.FC = () => {
  const { currentUser } = useApp();
  const [scoringRules, setScoringRules] = useState<ScoringRule[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [traits, setTraits] = useState<Trait[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [scales, setScales] = useState<Scale[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editRule, setEditRule] = useState<ScoringRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<ScoringRule | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  
  const [form, setForm] = useState<ScoringRuleForm>(DEFAULT_SCORING_RULE);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fetchedSections, fetchedTraits, fetchedExams, fetchedScales, fetchedRules] = await Promise.all([
        getSections(),
        getTraits(),
        getExams(),
        getScales({ isActive: true }),
        getScoringRules()
      ]);
      setSections(fetchedSections);
      setTraits(fetchedTraits);
      setExams(fetchedExams);
      setScales(fetchedScales.scales);
      setScoringRules(fetchedRules);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (section: string, field: string, value: any) => {
    if (section === "rawScoreCalculation") {
      setForm(prev => ({
        ...prev,
        rawScoreCalculation: {
          ...prev.rawScoreCalculation,
          [field]: value
        }
      }));
    } else if (section === "reverseScoring") {
      setForm(prev => ({
        ...prev,
        reverseScoring: {
          ...prev.reverseScoring,
          [field]: value
        }
      }));
    } else if (section === "traitAggregation") {
      setForm(prev => ({
        ...prev,
        traitAggregation: {
          ...prev.traitAggregation,
          [field]: value
        }
      }));
    } else if (section === "normalization") {
      setForm(prev => ({
        ...prev,
        normalization: {
          ...prev.normalization,
          [field]: value
        }
      }));
    } else if (section === "sectionScoring") {
      setForm(prev => ({
        ...prev,
        sectionScoring: {
          ...prev.sectionScoring,
          [field]: value
        }
      }));
    } else if (section === "overallIndex") {
      setForm(prev => ({
        ...prev,
        overallIndex: {
          ...prev.overallIndex,
          [field]: value
        }
      }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleScopeChange = (value: string) => {
    const scope = value as "global" | "section" | "trait";
    setForm(prev => ({ ...prev, scope, scopeId: "" }));
  };

  const calculatePreview = () => {
    const sampleScores = [1, 2, 3, 4, 5];
    
    let rawScore = 0;
    if (form.rawScoreCalculation.method === "sum") {
      rawScore = sampleScores.reduce((a, b) => a + b, 0);
    } else if (form.rawScoreCalculation.method === "average") {
      rawScore = sampleScores.reduce((a, b) => a + b, 0) / sampleScores.length;
    } else if (form.rawScoreCalculation.method === "weighted_sum") {
      rawScore = sampleScores.reduce((a, b) => a + b, 0);
    } else {
      rawScore = sampleScores.reduce((a, b) => a + b, 0) / sampleScores.length;
    }
    
    let normalizedScore = rawScore;
    if (form.normalization.enabled) {
      normalizedScore = ((rawScore - form.normalization.minValue) / 
        (form.normalization.maxValue - form.normalization.minValue)) * 
        (form.normalization.targetMax - form.normalization.targetMin) + 
        form.normalization.targetMin;
    }
    
    setPreviewData({
      rawScore: rawScore.toFixed(2),
      normalizedScore: normalizedScore.toFixed(2),
      formula: `(${rawScore.toFixed(2)} - ${form.normalization.minValue}) / (${form.normalization.maxValue} - ${form.normalization.minValue}) × (${form.normalization.targetMax} - ${form.normalization.targetMin}) + ${form.normalization.targetMin} = ${normalizedScore.toFixed(2)}`
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Rule name is required");
      return;
    }

    try {
      if (editRule) {
        await updateScoringRule(editRule.id, form);
        toast.success("Scoring rule updated successfully");
      } else {
        await createScoringRule(form, currentUser?.id || "system");
        toast.success("Scoring rule created successfully");
      }
      await loadData();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error saving scoring rule:", error);
      toast.error("Failed to save scoring rule");
    }
  };

  const handleDelete = async () => {
    if (!deleteRule) return;
    try {
      await deleteScoringRule(deleteRule.id);
      toast.success("Scoring rule deleted successfully");
      await loadData();
      setDeleteRule(null);
    } catch (error) {
      console.error("Error deleting scoring rule:", error);
      toast.error("Failed to delete scoring rule");
    }
  };

  const resetForm = () => {
    setForm(DEFAULT_SCORING_RULE);
    setEditRule(null);
    setPreviewData(null);
  };

  const openNew = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (rule: ScoringRule) => {
    setEditRule(rule);
    setForm({
      name: rule.name,
      description: rule.description,
      scope: rule.scope,
      scopeId: rule.scopeId || "",
      rawScoreCalculation: rule.rawScoreCalculation,
      reverseScoring: rule.reverseScoring,
      traitAggregation: rule.traitAggregation,
      normalization: rule.normalization,
      sectionScoring: rule.sectionScoring,
      overallIndex: rule.overallIndex,
      isActive: rule.isActive
    });
    setShowModal(true);
  };

  const columns: Column<ScoringRule>[] = [
    { key: "name", label: "Rule Name", render: (v, item) => (
      <div>
        <span className="font-semibold text-gray-800">{v}</span>
        {item.isActive && (
          <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
        )}
      </div>
    )},
    { key: "scope", label: "Scope", render: (v) => (
      <Badge text={v} variant={v === "global" ? "purple" : v === "section" ? "blue" : "green"} />
    )},
    { key: "scopeName", label: "Applied To", render: (v) => v || "All Assessments" },
    { key: "rawScoreCalculation", label: "Raw Score", render: (v) => v.method },
    { key: "normalization", label: "Normalization", render: (v) => v.enabled ? `${v.method} (${v.targetMin}-${v.targetMax})` : "Disabled" },
    { key: "overallIndex", label: "Overall Index", render: (v) => v.enabled ? "Enabled" : "Disabled" },
    { key: "updatedAt", label: "Last Updated", render: (v) => new Date(v).toLocaleDateString() }
  ];

  const scopeOptions = [
    { value: "global", label: "🌍 Global (All Assessments)" },
    { value: "section", label: "📋 Section Specific" },
    { value: "trait", label: "🧠 Trait Specific" }
  ];

  const methodOptions = [
    { value: "sum", label: "Sum of values" },
    { value: "average", label: "Average of values" },
    { value: "weighted_sum", label: "Weighted sum" },
    { value: "custom", label: "Custom formula" }
  ];

  const normalizationMethods = [
    { value: "min_max", label: "Min-Max Normalization (0-100)" },
    { value: "z_score", label: "Z-Score Standardization" },
    { value: "percentile", label: "Percentile Ranking" }
  ];

  const traitAggregationMethods = [
    { value: "average", label: "Average of trait questions" },
    { value: "sum", label: "Sum of trait questions" },
    { value: "weighted_average", label: "Weighted average" }
  ];

  return (
    <PageShell 
      title="Scoring & Normalization Rules" 
      subtitle="Configure how raw scores are calculated, normalized, and aggregated into final psychometric indices"
      action={{ label: "New Rule", onClick: openNew }}
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active Rules" value={scoringRules.filter(r => r.isActive).length} icon="📊" color="#6366f1" />
        <StatCard label="Global Rules" value={scoringRules.filter(r => r.scope === "global").length} icon="🌍" color="#10b981" />
        <StatCard label="Section Rules" value={scoringRules.filter(r => r.scope === "section").length} icon="📋" color="#f59e0b" />
        <StatCard label="Trait Rules" value={scoringRules.filter(r => r.scope === "trait").length} icon="🧠" color="#8b5cf6" />
      </div>

      <GlassCard className="p-4 mb-6">
        <div className="flex items-start gap-3">
          <span className="text-2xl">📐</span>
          <div>
            <p className="text-sm font-bold text-gray-700">Scoring Formula Guide</p>
            <p className="text-xs text-gray-500 mt-0.5">
              <strong>Raw Score</strong> = Sum/Average of question values<br/>
              <strong>Normalized Score</strong> = ((Raw Score - Min) / (Max - Min)) × (Target Max - Target Min) + Target Min<br/>
              <strong>Trait Score</strong> = Average of all questions mapped to that trait<br/>
              <strong>Section Score</strong> = Weighted average of trait scores in the section<br/>
              <strong>Overall Index</strong> = Weighted sum of section scores
            </p>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-5">
        <Table 
          columns={columns} 
          data={scoringRules} 
          onEdit={openEdit} 
          onDelete={(rule) => setDeleteRule(rule)} 
        />
        {scoringRules.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            No scoring rules found. Click "New Rule" to create your first scoring configuration.
          </div>
        )}
      </GlassCard>

      {/* Scoring Rule Modal */}
      <Modal 
        isOpen={showModal} 
        onClose={() => { setShowModal(false); resetForm(); }} 
        title={editRule ? "Edit Scoring Rule" : "Create Scoring Rule"}
        size="xl"
      >
        <div className="space-y-5 max-h-[80vh] overflow-y-auto px-1 custom-scrollbar">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">📝</span>
                Basic Information
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Input 
                label="Rule Name" 
                value={form.name} 
                onChange={(v) => handleFormChange("", "name", v)} 
                required 
                placeholder="e.g., Standard Psychometric Scoring"
              />
              <Textarea 
                label="Description" 
                value={form.description} 
                onChange={(v) => handleFormChange("", "description", v)} 
                placeholder="Describe when and how this scoring rule should be applied..."
                rows={2}
              />
              <Select 
                label="Scope" 
                value={form.scope} 
                onChange={handleScopeChange} 
                options={scopeOptions}
              />
              
              {form.scope === "section" && (
                <Select 
                  label="Select Section" 
                  value={form.scopeId} 
                  onChange={(v) => handleFormChange("", "scopeId", v)} 
                  options={[{ value: "", label: "-- Select Section --" }, ...sections.map(s => ({ value: s.id, label: s.name }))]}
                />
              )}
              
              {form.scope === "trait" && (
                <Select 
                  label="Select Trait" 
                  value={form.scopeId} 
                  onChange={(v) => handleFormChange("", "scopeId", v)} 
                  options={[{ value: "", label: "-- Select Trait --" }, ...traits.map(t => ({ value: t.id, label: `${ "🧠" } ${t.name}` }))]}
                />
              )}
              
              <Toggle 
                checked={form.isActive} 
                onChange={(v) => handleFormChange("", "isActive", v)} 
                label="Active (apply this rule to new assessments)"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">🔢</span>
                Raw Score Calculation
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Select 
                label="Calculation Method" 
                value={form.rawScoreCalculation.method} 
                onChange={(v) => handleFormChange("rawScoreCalculation", "method", v as any)} 
                options={methodOptions}
              />
              {form.rawScoreCalculation.method === "custom" && (
                <Textarea 
                  label="Custom Formula" 
                  value={form.rawScoreCalculation.formula} 
                  onChange={(v) => handleFormChange("rawScoreCalculation", "formula", v)} 
                  placeholder="e.g., (sum(q1,q2,q3) * 0.4) + (max(q4,q5) * 0.6)"
                  rows={2}
                />
              )}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">💡 Formula Preview:</p>
                <p className="text-xs text-blue-600 mt-1">
                  {form.rawScoreCalculation.method === "sum" && "Raw Score = Sum of all question values"}
                  {form.rawScoreCalculation.method === "average" && "Raw Score = Average of all question values"}
                  {form.rawScoreCalculation.method === "weighted_sum" && "Raw Score = Sum(question_value × question_weightage)"}
                  {form.rawScoreCalculation.method === "custom" && form.rawScoreCalculation.formula}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">🔄</span>
                Reverse Scoring Configuration
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Toggle 
                checked={form.reverseScoring.enabled} 
                onChange={(v) => handleFormChange("reverseScoring", "enabled", v)} 
                label="Enable Reverse Scoring for negative traits"
              />
              {form.reverseScoring.enabled && (
                <>
                  <Input 
                    label="Reverse Scoring Formula" 
                    value={form.reverseScoring.formula} 
                    onChange={(v) => handleFormChange("reverseScoring", "formula", v)} 
                    placeholder="(maxScale + 1) - value"
                  />
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">📐 Example: If maxScale = 5, original value = 2</p>
                    <p className="text-xs text-blue-600 mt-1">Reverse Score = (5 + 1) - 2 = 4</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">📊</span>
                Trait Score Aggregation
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Select 
                label="Aggregation Method" 
                value={form.traitAggregation.method} 
                onChange={(v) => handleFormChange("traitAggregation", "method", v as any)} 
                options={traitAggregationMethods}
              />
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">📊 Trait Score Formula:</p>
                <p className="text-xs text-blue-600 mt-1">
                  {form.traitAggregation.method === "average" && "Trait Score = Sum(question_values) / Number of questions"}
                  {form.traitAggregation.method === "sum" && "Trait Score = Sum of all question values"}
                  {form.traitAggregation.method === "weighted_average" && "Trait Score = Sum(question_value × weightage) / Sum(weightage)"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">📈</span>
                Score Normalization (0-100 Scale)
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Toggle 
                checked={form.normalization.enabled} 
                onChange={(v) => handleFormChange("normalization", "enabled", v)} 
                label="Normalize scores to standardized scale"
              />
              {form.normalization.enabled && (
                <>
                  <Select 
                    label="Normalization Method" 
                    value={form.normalization.method} 
                    onChange={(v) => handleFormChange("normalization", "method", v as any)} 
                    options={normalizationMethods}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input 
                      label="Min Raw Score" 
                      type="number" 
                      value={String(form.normalization.minValue)} 
                      onChange={(v) => handleFormChange("normalization", "minValue", Number(v))} 
                    />
                    <Input 
                      label="Max Raw Score" 
                      type="number" 
                      value={String(form.normalization.maxValue)} 
                      onChange={(v) => handleFormChange("normalization", "maxValue", Number(v))} 
                    />
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-sm text-purple-800 font-semibold">Normalization Formula (Min-Max):</p>
                    <p className="text-xs text-purple-600 mt-1 font-mono">
                      Normalized Score = ((Raw Score - Min) / (Max - Min)) × (Target Max - Target Min) + Target Min
                    </p>
                    <p className="text-xs text-purple-600 mt-2">
                      Target Range: {form.normalization.targetMin} - {form.normalization.targetMax}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="text-xl">🎯</span>
                Section & Overall Scoring
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <Toggle 
                checked={form.sectionScoring.calculateSectionWise} 
                onChange={(v) => handleFormChange("sectionScoring", "calculateSectionWise", v)} 
                label="Calculate section-wise scores"
              />
              <Toggle 
                checked={form.overallIndex.enabled} 
                onChange={(v) => handleFormChange("overallIndex", "enabled", v)} 
                label="Calculate Overall Psychometric Index"
              />
              {form.overallIndex.enabled && (
                <>
                  <Textarea 
                    label="Overall Index Formula" 
                    value={form.overallIndex.formula} 
                    onChange={(v) => handleFormChange("overallIndex", "formula", v)} 
                    placeholder="(sum(section_scores × section_weightage) / sum(weightage)) × 100"
                    rows={2}
                  />
                  <Textarea 
                    label="Interpretation Guide" 
                    value={form.overallIndex.interpretation} 
                    onChange={(v) => handleFormChange("overallIndex", "interpretation", v)} 
                    placeholder="0-25: Low, 26-50: Moderate, 51-75: High, 76-100: Very High"
                    rows={2}
                  />
                </>
              )}
            </div>
          </div>

          {form.normalization.enabled && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-5 py-3 border-b border-green-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <span className="text-xl">🔍</span>
                  Live Preview
                </h3>
              </div>
              <div className="p-5">
                <Button onClick={calculatePreview} variant="secondary" size="sm" className="mb-3">
                  Calculate Preview
                </Button>
                {previewData && (
                  <div className="space-y-2">
                    <p className="text-sm"><strong>Sample Raw Score:</strong> {previewData.rawScore}</p>
                    <p className="text-sm"><strong>Normalized Score:</strong> <span className="text-green-700 font-bold">{previewData.normalizedScore}</span></p>
                    <p className="text-xs text-gray-600 mt-2"><strong>Formula Applied:</strong> {previewData.formula}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 sticky bottom-0 bg-white py-4">
            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSave} className="bg-gradient-to-r from-indigo-600 to-purple-600">
              {editRule ? "Update Rule" : "Create Rule"}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog 
        isOpen={!!deleteRule} 
        onClose={() => setDeleteRule(null)}
        onConfirm={handleDelete}
        title="Delete Scoring Rule" 
        message={`Are you sure you want to delete "${deleteRule?.name}"? This may affect scoring for existing assessments.`}
      />
    </PageShell>
  );
};

const Badge: React.FC<{ text: string; variant?: string }> = ({ text, variant = "default" }) => {
  const variants: Record<string, string> = {
    default: "bg-gray-100 text-gray-700",
    purple: "bg-purple-100 text-purple-700",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-green-100 text-green-700"
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${variants[variant] || variants.default}`}>
      {text}
    </span>
  );
};

export default ScoringRulesPage;